import { keepPreviousData, useQuery } from '@tanstack/react-query';
import React, { useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';

import { Icon } from '@column/column-ui-kit';
import { Badge, DataTable, type DataTableColumnDef } from '@column/ui';

import { ROUTE } from '~/app/routes';
import { Loading, Section } from '~/components';
import { Empty as TableEmpty, getTimestampColumn, tableDefaults, useTimeFormat } from '~/components/Table';
import { usePaginationState } from '~/lib/api/hooks/usePaginationState';
import { useNavigate } from '~/lib/navigation';
import { Card, CardRepository } from '~/repositories/CardRepository';
import { TableTimeFormat } from '~/repositories/UserRepository';
import { useSessionStore } from '~/stores/Session';
import { Inner } from '~/styles';
import { formatString } from '~/util';

import { CardAccountProps } from './CardAccount';

const getColumns = (
  timeFormat: TableTimeFormat,
  onTimeFormatChange: (value: TableTimeFormat) => void
): DataTableColumnDef<Card>[] => [
  {
    header: 'Authorized User',
    accessor: 'authorizedUserEntityId',
    width: 'minmax(140px, 2fr)',
    cell: ({ value }) => <DataTable.TextCell weight="medium" value={value || '-'} />,
  },
  {
    header: 'BIN',
    accessor: 'bin',
    width: 'minmax(min-content, 1fr)',
    cell: ({ value }) => <DataTable.TextCell value={value || '-'} />,
  },
  {
    header: 'Last 4',
    accessor: 'lastFourDigits',
    width: 'minmax(min-content, 1fr)',
    cell: ({ value }) => <DataTable.TextCell value={value || '-'} />,
  },
  {
    header: 'Expires',
    accessor: 'expiresAt',
    width: 'minmax(min-content, 1fr)',
    cell: ({ value }) => <DataTable.TextCell value={value || '-'} />,
  },
  {
    header: 'Status',
    accessor: 'status',
    width: 'minmax(min-content, 1fr)',
    cell: ({ value }) => <Badge variant="secondary">{value ? formatString(value) : '-'}</Badge>,
  },
  {
    header: 'Type',
    accessor: 'type',
    width: 'minmax(min-content, 1fr)',
    cell: ({ value }) => <Badge variant="secondary">{value ? formatString(value) : '-'}</Badge>,
  },
  getTimestampColumn<Card>({
    accessor: 'createdAt',
    timeFormat,
    onTimeFormatChange,
    width: 'minmax(min-content, 1fr)',
    fallback: '-',
  }),
];

export const PageCardAccountCards: React.FC = () => {
  const navigate = useNavigate();
  const { cardAccount } = useOutletContext<CardAccountProps>();
  const { currentPlatform, isSandbox } = useSessionStore();
  const {
    cursor,
    hasPrevious,
    onNext,
    onPrevious,
    pageIndex,
    pageSize,
    reset: resetPagination,
    setPageSize,
  } = usePaginationState();
  const { timeFormat, onTimeFormatChange } = useTimeFormat('card-account-cards');

  const isInitialRenderRef = useRef(true);
  useEffect(() => {
    if (isInitialRenderRef.current) {
      isInitialRenderRef.current = false;
      return;
    }
    resetPagination();
  }, [cardAccount?.id, currentPlatform?.id, isSandbox, resetPagination]);

  const {
    data: cardsData,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ['card-account-cards', cardAccount?.id, currentPlatform?.id, isSandbox, pageSize, cursor],
    queryFn: () => CardRepository.listCards({ cardAccountId: cardAccount.id, limit: pageSize, startingAfter: cursor }),
    enabled: !!cardAccount?.id,
    placeholderData: keepPreviousData,
  });

  const cards = cardsData?.cards ?? [];
  const hasMore = Boolean(cardsData?.hasMore);

  return (
    <Inner p={0}>
      <Section headline="Cards">
        <Loading isLoading={isLoading}>
          <Inner px="var(--page-margin)" py={0}>
            <DataTable<Card>
              {...tableDefaults}
              columns={getColumns(timeFormat, onTimeFormatChange)}
              data={cards}
              rowKey={(row) => row.id}
              isLoading={isFetching}
              emptyContent={
                <TableEmpty
                  icon={<Icon.AnimationWallet />}
                  title="No cards yet"
                  description="Cards will appear here for this card account."
                />
              }
              pagination={{
                hasNext: hasMore,
                hasPrevious,
                pageIndex,
                pageSize,
                onNext: () => {
                  if (cards.length > 0) {
                    onNext({ cursor: cards[cards.length - 1].id });
                  }
                },
                onPrevious,
                onPageSizeChange: setPageSize,
              }}
              onRowClick={(row) => {
                navigate(`${ROUTE.CARDS}/edit/${row.id}`);
              }}
            />
          </Inner>
        </Loading>
      </Section>
    </Inner>
  );
};
